import { forwardRef, memo, useCallback, useImperativeHandle, useState } from "react";
import { Button } from "@common";

const OPTION_TITLES = {
  all: "Show all DDS associate fee rows",
  completed:
    "Show practitioners with monthly totals data and monthly reconciliation Production, Collection, and Hours Worked all greater than 0",
  not_completed:
    "Show practitioners with monthly totals data but monthly reconciliation Production, Collection, or Hours Worked blank or less than or equal to 0",
  not_worked:
    "Show practitioners with no monthly totals data and no monthly reconciliation Production, Collection, or Hours Worked data",
  data_mismatch:
    "Show practitioners with no monthly totals data but with monthly reconciliation data",
};

const OPTION_BUTTON_VARIANTS = {
  all: "primary",
  completed: "success",
  not_completed: "secondary",
  not_worked: "gray",
  data_mismatch: "danger",
};

/**
 * Isolated filter-bar: owns its "selected button" UI state internally so
 * that clicking a button only re-renders this component, not the parent
 * table. Parent still drives data filtering via the onChange callback
 * (which it wraps in startTransition).
 *
 * Use the ref.reset() imperative API to force the bar back to "all"
 * (e.g. from a "Clear all filters" button) without causing parent re-render.
 */
const DdsCompletionFilterBar = memo(
  forwardRef(function DdsCompletionFilterBar({ options, counts, onChange, initialValue }, ref) {
    const [selected, setSelected] = useState(initialValue ?? "all");

    const handleClick = useCallback(
      (value) => {
        if (selected === value) return;
        setSelected(value);
        onChange?.(value);
      },
      [selected, onChange],
    );

    useImperativeHandle(
      ref,
      () => ({
        reset: () => setSelected("all"),
        getValue: () => selected,
      }),
      [selected],
    );

    return (
      <div className="flex items-center gap-1 rounded-lg border border-palette-slate-divider bg-palette-slate-soft p-1">
        {options.map((option) => {
          const isSelected = selected === option.value;
          const selectedVariant = OPTION_BUTTON_VARIANTS[option.value] ?? "primary";

          return (
            <Button
              key={option.value}
              variant={isSelected ? selectedVariant : "outline"}
              size="sm"
              onClick={() => handleClick(option.value)}
              title={OPTION_TITLES[option.value]}
            >
              {`${option.label} (${counts[option.value] ?? 0})`}
            </Button>
          );
        })}
      </div>
    );
  }),
);

export default DdsCompletionFilterBar;
